/**
 * Centre d'export transverse — client API (DOC-003bis Phase 5).
 *
 * Routes `/v1/exports` : suivi des exports DE L'UTILISATEUR (tous producteurs).
 * Pas d'en-tête X-Entity-Id (transverse). Le déclenchement d'un export GED vit
 * dans `ged-export.ts` (`/v1/ged/export`).
 *
 * ⚠️ Un refus d'un GESTE (supprimer, relancer, télécharger) remonte en `ApiError`
 * (`handleApiError`) : un `Error` nu ne gardait que la phrase du serveur, et la
 * page « Exports » affichait « Permission '…' required for this action » en
 * anglais brut — sans le CODE, elle ne pouvait pas le dire dans sa langue
 * (`useExports.ts`, table transverse), ni offrir la référence au support.
 */

import i18n from '@/i18n'
import { API_BASE, apiFetch } from '@/lib/api'
import { handleApiError } from '@/lib/api-error'
import type {
    ExportJob,
    ExportListCursor,
    ExportListResponse,
    GedExportResponse,
} from '@/hooks/useExports.types'

const EXPORTS_BASE = `${API_BASE}/v1/exports`

export async function fetchExports(cursor?: ExportListCursor | null): Promise<ExportListResponse> {
    const params = new URLSearchParams()
    if (cursor?.cursor_created_at) params.set('cursor_created_at', cursor.cursor_created_at)
    if (cursor?.cursor_id) params.set('cursor_id', cursor.cursor_id)
    const qs = params.toString()
    const res = await apiFetch(`${EXPORTS_BASE}/jobs${qs ? `?${qs}` : ''}`)
    if (!res.ok) throw new Error(`Failed to fetch exports: ${res.status}`)
    return res.json()
}

export async function fetchExportJob(jobId: string): Promise<ExportJob> {
    const res = await apiFetch(`${EXPORTS_BASE}/jobs/${jobId}`)
    if (!res.ok) throw new Error(`Export not found: ${res.status}`)
    return res.json()
}

export async function fetchExportsUnseenCount(): Promise<number> {
    const res = await apiFetch(`${EXPORTS_BASE}/unseen-count`)
    if (!res.ok) throw new Error(`Failed to fetch unseen count: ${res.status}`)
    const data = await res.json()
    return data.count ?? 0
}

export async function deleteExportJob(jobId: string): Promise<void> {
    const res = await apiFetch(`${EXPORTS_BASE}/jobs/${jobId}`, { method: 'DELETE' })
    if (!res.ok) await handleApiError(res)
}

/** `lang` : la langue de l'ÉCRAN, comme à la création — le refus du plafond d'un
 *  export groupé se lit dans cette langue, pas dans celle du job d'origine. */
export async function relaunchExportJob(jobId: string, lang?: string): Promise<GedExportResponse> {
    const qs = lang ? `?lang=${encodeURIComponent(lang)}` : ''
    const res = await apiFetch(`${EXPORTS_BASE}/jobs/${jobId}/relaunch${qs}`, { method: 'POST' })
    if (!res.ok) await handleApiError(res)
    return res.json()
}

/** La langue de l'ÉCRAN, jointe au téléchargement : le serveur y rédige ses refus
 *  (fichier expiré, pas encore prêt) — sans elle, il répondait en français à l'écran
 *  anglais. `/download-url` n'en a pas besoin : son échec est muet, et le repli
 *  `/download` dit le refus. */
function dansLaLangueDeLEcran(url: string): string {
    return `${url}?lang=${encodeURIComponent(i18n.language || 'fr')}`
}

/**
 * URL du endpoint de téléchargement authentifié.
 */
export function exportDownloadUrl(jobId: string): string {
    return dansLaLangueDeLEcran(`${EXPORTS_BASE}/jobs/${jobId}/download`)
}

/** URL de téléchargement d'UN volume d'un export multi-volumes. */
export function exportVolumeDownloadUrl(jobId: string, seq: number): string {
    return dansLaLangueDeLEcran(`${EXPORTS_BASE}/jobs/${jobId}/volumes/${seq}/download`)
}

// Format de l'objet exporté → extension de fichier (miroir du backend
// `_FORMAT_EXTENSION`). `zip` = archive (GED/RGPD, multi-volumes) ; csv/xlsx/json/xml
// = exports tabulaires ; `pdf` = document déjà rendu que le centre transporte
// (dossier de preuve CHK-015). Repli `zip` (rétro-compat des producteurs archive).
//
// ⚠️ **Un format manquant ici est MUET** : le fichier s'enregistre en `.zip` et
// ne s'ouvre nulle part. Un gardien serveur compare cette table à son originale
// (`test_export_format_miroir_front.py`) — la divergence ne se voit pas à l'œil.
const _FORMAT_EXT: Record<string, string> = {
    zip: 'zip', csv: 'csv', xlsx: 'xlsx', json: 'json', xml: 'xml', pdf: 'pdf',
}
function formatExtension(format?: string | null): string {
    return _FORMAT_EXT[(format || 'zip').toLowerCase()] ?? 'zip'
}

/**
 * REPLI du nom de fichier, quand le serveur n'en a donné aucun : le MIROIR de
 * `api/_exports_nom_du_fichier.nom_du_fichier` — « {libellé en minuscules, mots
 * reliés par des tirets}-{AAAA-MM-JJ}[-volNN].{ext} » (décision PO du
 * 2026-09-23 : « frais-2026-09-23.xlsx »).
 *
 * 🔴 Il écrivait « Frais - 2026-09-23.xlsx » quand le serveur signait
 * « frais.xlsx » : le même export ne s'appelait pas pareil selon que l'URL
 * présignée répondait ou non. Il reprend désormais la règle du serveur, lettre
 * pour lettre (accents gardés, NFC, 60 caractères).
 *
 * ⚠️ Un miroir, donc un SECOND choix : le serveur date le fichier au jour de
 * l'ORGANISME, le repli au jour du POSTE (le seul fuseau que le navigateur
 * connaisse). C'est pourquoi le nom du serveur passe TOUJOURS avant — la route
 * `/download-url` le rend, et la liste le porte pour chaque volume.
 */
function exportFilename(
    label?: string | null, dateIso?: string | null, seq?: number, format?: string | null,
): string {
    const libelle = (label || 'export').normalize('NFC').trim().toLowerCase()
    const mots = libelle.replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-+|-+$/g, '')
    // Tronqué PAR POINT DE CODE (comme `[:60]` en Python), puis le tiret final
    // retiré : il doublerait celui de la date.
    const slug = Array.from(mots).slice(0, 60).join('').replace(/-+$/, '') || 'export'
    const d = dateIso ? new Date(dateIso) : new Date()
    const jour = Number.isNaN(d.getTime()) ? new Date() : d
    const date = [
        jour.getFullYear(),
        String(jour.getMonth() + 1).padStart(2, '0'),
        String(jour.getDate()).padStart(2, '0'),
    ].join('-')
    const vol = seq ? `-vol${String(seq).padStart(2, '0')}` : ''
    return `${slug}-${date}${vol}.${formatExtension(format)}`
}

/** Cœur de téléchargement FIABLE (cross-origin + Safari) : `fetch` authentifié →
 *  Blob → ancre `download` (évite le fichier « .download » d'une navigation directe).
 *  ⚠️ Bufferise le fichier ENTIER en mémoire JS → réservé aux PETITS fichiers / au
 *  repli dev. Les gros exports passent par `_downloadNative` (présigné, flux disque). */
async function _downloadToFile(url: string, filename: string): Promise<void> {
    const res = await apiFetch(url)
    if (!res.ok) await handleApiError(res)
    const blob = await res.blob()
    const objectUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
}

/** Téléchargement NATIF depuis une URL présignée S3 (Content-Disposition: attachment
 *  signé dans l'URL) : le navigateur streame DIRECTEMENT vers le disque, RIEN en
 *  mémoire JS. Pas d'attribut `download` (ignoré en cross-origin) — le nom vient de
 *  la disposition signée. Indispensable aux gros exports (200k lignes ≈ 100+ Mo). */
function _downloadNative(presignedUrl: string): void {
    const a = document.createElement('a')
    a.href = presignedUrl
    a.rel = 'noopener'
    document.body.appendChild(a)
    a.click()
    a.remove()
}

/** Télécharge le fichier d'un export MONO-volume. Tente d'abord le téléchargement
 *  NATIF (présigné, sans bufferisation navigateur) ; repli sur le streaming proxifié
 *  par l'API en dev (mode local, pas de S3) ou en cas d'échec d'obtention de l'URL. */
export async function downloadExport(
    jobId: string,
    label?: string | null,
    dateIso?: string | null,
    format?: string | null,
    /** Nom IMPOSÉ, quand le producteur en détient un qui fait foi (miroir de
     *  `scope_params.file_name` côté serveur). Un dossier de preuve est déposé au
     *  coffre sous un nom précis et cité sous ce nom dans le journal des actes :
     *  sa copie téléchargée doit porter le même, sinon on ne sait plus rapprocher
     *  la pièce d'elle-même. Sans lui, le nom se dérive du libellé + la date.
     *  La page Exports y passe le `file_name` que la liste porte : c'est le nom
     *  du serveur, qui tient même quand `/download-url` échoue.
     *
     *  ⚠️ N'agit que sur le REPLI proxifié — le chemin natif (présigné) reçoit son
     *  nom du `Content-Disposition` signé par le serveur, qui applique la même
     *  règle. C'est en développement (pas de S3) que le repli sert, donc là que la
     *  divergence se serait vue en premier. */
    explicitFilename?: string | null,
): Promise<void> {
    // 🔴 **Le repli nommait le fichier autrement que la production.** Le serveur
    // SAIT le nom qu'il donne (`_export_filename`, `scope_params.file_name`) et
    // le rend dans `/download-url` à côté de l'URL — le front ne le lisait pas,
    // et dérivait « Libellé - AAAA-MM-JJ.ext » du libellé. Le même export
    // s'enregistrait donc « commissions_2026-09-01.xlsx » en production (nom
    // signé dans le `Content-Disposition`) et « Éléments variables de paie — du
    // 01 09 2026 au 30 09 2026 - 2026-09-22.xlsx » en développement.
    let nomDuServeur: string | null = null
    try {
        const res = await apiFetch(`${EXPORTS_BASE}/jobs/${jobId}/download-url`)
        if (res.ok) {
            const data: { url?: string | null; filename?: string | null } = await res.json()
            if (data.url) {
                _downloadNative(data.url)
                return
            }
            nomDuServeur = data.filename?.trim() || null
        }
    } catch {
        // Repli ci-dessous (streaming proxifié), sur le nom dérivé du libellé.
    }
    await _downloadToFile(
        exportDownloadUrl(jobId),
        explicitFilename?.trim() ||
            nomDuServeur ||
            exportFilename(label, dateIso, undefined, format),
    )
}

/** Télécharge UN volume d'un export multi-volumes, sous le nom que le SERVEUR
 *  lui donne (`fileName`, lu dans la liste : `volumes[].file_name`).
 *
 *  🔴 Il se nommait ici d'après le libellé (« Clients - 2026-09-22 - vol01.zip »)
 *  quand la route du volume l'envoyait sous un autre nom : aucune route ne
 *  rendait ce nom au front, et l'en-tête `Content-Disposition` ne se lisait pas
 *  en production (API sur une autre origine). Le miroir ne sert plus qu'à un
 *  job lu avant que le serveur ne porte ce nom. */
export async function downloadExportVolume(
    jobId: string,
    seq: number,
    label?: string | null,
    dateIso?: string | null,
    fileName?: string | null,
): Promise<void> {
    await _downloadToFile(
        exportVolumeDownloadUrl(jobId, seq),
        fileName?.trim() || exportFilename(label, dateIso, seq),
    )
}
